import "babel-polyfill";
import "whatwg-fetch";
import get from "lodash/get";
import {hot} from "react-hot-loader/root";
import {BrowserRouter} from "react-router-dom";
import {compose} from "redux";
import {connect} from "react-redux";
import {graphql, withApollo} from "react-apollo";
import React, {useEffect, useState} from "react";
import {withRouter} from "react-router";
import mixpanel from "mixpanel-browser";

import {COOKIE_CATEGORIES, MIXPANEL} from "shared/Utils/Constants";
import mixpanelAdaptor from "shared/AnalyticsNew/AnalyticsProvider/Adaptors/mixpanelAdaptor";
import {getConsents} from "shared/CookieManager/CookieManager";
import UniversalAnalyticsProvider from "shared/AnalyticsNew/AnalyticsProvider/UniversalAnalyticsProvider";
import AuthProvider from "shared/Auth/AuthProvider/AuthProvider";
import ErrorBoundary from "shared/General/components/ErrorBoundary/ErrorBoundary";
import ErrorReportingProvider from "shared/General/components/ErrorReporting/ErrorReportingProvider";
import {DesignSystemProvider} from "@unibuddy/patron";
import {
    apps,
    errorReportingAdaptor,
    initErrorReportingService,
} from "shared/General/components/ErrorReporting/errorReportingAdaptor";
import withAuth from "shared/Auth/withAuth/withAuth";
import SocketProvider from "shared/Experimental/Sockets/SocketProvider/SocketProvider";
import UserPermissionProvider from "shared/User/UserPermissionProvider/UserPermissionProvider";
import useAnalytics from "shared/AnalyticsNew/hooks/useAnalytics";
import {productSpec} from "shared/AnalyticsNew/constants/productSpecification";
import GetUniversityQuery from "shared/University/queries/GetUniversityQuery";
import Intl, {Constants, I18nextEngine} from "@unibuddy/intl";
import {createFeatureFlaggingProvider, User} from "ub-feature-flagging-react";
import Navigation from "./modules/Navigation/Navigation";
import AppRoutes from "./AppRoutes";
import s from "./App.pcss";
import "./main.css";

// Initialise Sentry
initErrorReportingService({app: apps.AMBASSADOR_DASHBOARD});

// Initialise i18next
const options = {
    appName: Constants.APP_NAMES.AMBASSADOR_DASHBOARD,
    lng: Constants.DEFAULT_LOCALE,
    translationsUrl: get(window, "env.TRANSLATIONS_URL"),
};
const i18nextEngine = new I18nextEngine(options);

const FeatureFlaggingProvider = createFeatureFlaggingProvider(
    window.env.UB_LAUNCHDARKLY_CLIENT_ID || "",
);

const customButtonTheme = {
    Button: {
        colors: {
            primary: {
                bgColor: "#2266AA",
                hoverBgColor: "#1A72CB",
                clearColor: "#2266AA",
                clearHoverBgColor: "#E9F0F6",
            },
        },
    },
    TextInput: {
        placeholderColor: "#585858",
    },

};

export const initMixpanel = (preferences) => {
    const adaptors = [];
    let mixpanelInit = false;
    const {MIXPANEL_ID} = window.env;
    preferences.forEach((preference) => {
        if (preference) {
            const key = preference.trim().toLowerCase();
            if (
                !mixpanelInit &&
                COOKIE_CATEGORIES.has(key) &&
                COOKIE_CATEGORIES.get(key).includes(MIXPANEL)
            ) {
                mixpanel.init(MIXPANEL_ID, {
                    api_host: process.env.MIXPANEL_PROXY_HOST,
                });
                adaptors.push(mixpanelAdaptor);
                mixpanelInit = true;
            }
        }
    });
    return adaptors;
};

export const ProductOpenedWrapper = ({authState, children}) => {
    const {
        events: {productOpened},
    } = useAnalytics();
    useEffect(
        () => {
            productOpened({
                name: productSpec.ambassadorDashboard.NAME,
                universitySlug:
                    get(authState, "me.mentor.university.slug") ||
                    get(authState, "me.staff.university.slug") ||
                    "Not logged in",
            });
        },
        [productOpened], // eslint-disable-line react-hooks/exhaustive-deps
    );
    return children;
};

const App = (props) => {
    const [analyticsAdaptors, setAnalyticsAdaptors] = useState([]);

    // We are using setState inside this because this useEffect will only be called once.
    useEffect(() => {
        const adaptors = [];
        const preferences = getConsents();
        adaptors.push(initMixpanel(preferences));
        setAnalyticsAdaptors(...adaptors);
    }, []);

    const user = new User(`ub-ambassador-dashboard-${get(props, "university.slug")}`, {
        university: {
            id: get(props, "university.id"),
            slug: get(props, "university.slug"),
            name: get(props, "university.name"),
        },
    });

    return (
        <ErrorReportingProvider adaptor={errorReportingAdaptor}>
            <ErrorBoundary>
                <BrowserRouter basename={window.env.base_path ?? "/"}>
                    <AuthProvider>
                        <UniversalAnalyticsProvider
                            adaptors={analyticsAdaptors}
                            user={get(props, "authState.me.anyUser")}
                        >
                            <UserPermissionProvider>
                                <ProductOpenedWrapper
                                    authState={props.authState}
                                >
                                    <SocketProvider>
                                        <DesignSystemProvider theme={customButtonTheme}>
                                            <FeatureFlaggingProvider user={user}>
                                                <div className={s.container}>
                                                    <Intl
                                                        engine={i18nextEngine}
                                                        crowdinProjectName={
                                                            Constants
                                                                .CROWDIN_PROJECT_NAMES
                                                                .AMBASSADOR_DASHBOARD
                                                        }
                                                    >
                                                        <Navigation
                                                            university={
                                                                props.university
                                                            }
                                                        />
                                                        <AppRoutes
                                                            university={
                                                                props.university
                                                            }
                                                        />
                                                    </Intl>
                                                </div>
                                            </FeatureFlaggingProvider>
                                        </DesignSystemProvider>
                                    </SocketProvider>
                                </ProductOpenedWrapper>
                            </UserPermissionProvider>
                        </UniversalAnalyticsProvider>
                    </AuthProvider>
                </BrowserRouter>
            </ErrorBoundary>
        </ErrorReportingProvider>
    );
};

const universityQueryOptions = {
    name: "data",
    options(props) {
        return {
            variables: {
                slug:
                    get(props, "me.mentor.university.slug") ||
                    get(props, "me.staff.university.slug"),
            },
            fetchPolicy: "cache-and-network",
        };
    },
    props({data: {university}}) {
        return {
            university,
        };
    },
};

export default compose(
    hot,
    withRouter,
    connect(),
    withAuth,
    withApollo,
    graphql(GetUniversityQuery, universityQueryOptions),
)(App);
